import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from 'components/Layout';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  TextField,
  Typography,
  Button as MuiButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMst } from 'model/Root';
import type { RootInstance } from 'model/Root';
import DOMPurify from 'dompurify';
import { getVoicePreview, listVoicesElevenLabs } from 'services/csAudiobookApi';
import { getAudiobookDetailsForImport } from 'services/audiobookApi';
import type { CsChapter, CsAudiobookListItem } from 'types/CsAudiobook';
import type { Chapter } from 'types';
import { useTranslation } from 'react-i18next';

type Voice = { voice_id: string; name: string };

type AudioRunDetail = {
  id?: string;
  status?: string;
  totalChapters?: number;
  completedChapters?: number;
  failedChapters?: number;
  lastMessage?: string;
  lastActivityMessage?: string;
  lastActivityAt?: string;
  chapters?: Array<{
    chapterId?: string;
    processedUnits?: number;
    totalUnits?: number;
    status?: string;
    stage?: string;
  }>;
};

function statusChipForChapter(ch: CsChapter, resolvedAudioUrl?: string) {
  const rawStatus = String(ch?.status ?? '').toLowerCase();
  if (rawStatus === 'processing' || rawStatus === 'audio_processing')
    return <Chip size="small" label={'Käsitellään'} color="warning" />;
  if (rawStatus === 'error' || rawStatus === 'audio_failed') return <Chip size="small" label={'Virhe'} color="error" />;

  const hasAudio = !!(resolvedAudioUrl && resolvedAudioUrl.length > 0);
  if (hasAudio) return <Chip size="small" label={'Valmis'} color="success" />;

  const hasCleaned = !!(ch?.cleaned_text && String(ch.cleaned_text).trim().length > 0);
  if (hasCleaned) return <Chip size="small" label={'Puhdistettu'} color="info" />;

  return <Chip size="small" label={'Tarkistus'} />;
}

const isRunActiveStatus = (status?: string | null) => {
  if (!status) return false;
  const normalized = String(status).toLowerCase();
  return normalized === 'running' || normalized === 'queued' || normalized === 'starting' || normalized === 'pending';
};

const SSML_BREAK_PATTERN = /<break\s+time="([\d.]+)s"\s*\/?>/i;
const NUMBER_PATTERN = /\d+(?:[.,]\d+)?/;

const escapeHtml = (input: string) =>
  String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeAndHighlightNumbers = (input: string) => {
  if (!input) return '';
  const numberRegex = new RegExp(NUMBER_PATTERN.source, 'g');
  let out = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = numberRegex.exec(input)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    out += escapeHtml(input.slice(lastIndex, start));
    out += `<span class="qa-number">${escapeHtml(match[0])}</span>`;
    lastIndex = end;
  }
  out += escapeHtml(input.slice(lastIndex));
  return out;
};

const renderSsmlBreaksToHtml = (input: string, opts?: { hideMarkers?: boolean }) => {
  if (!input) return '';
  const hideMarkers = !!opts?.hideMarkers;
  const ssmlBreakRegex = new RegExp(SSML_BREAK_PATTERN.source, 'gi');
  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ssmlBreakRegex.exec(input)) !== null) {
    const [full, secondsRaw] = match;
    const start = match.index;
    const plainSegment = input.slice(lastIndex, start);
    result += escapeAndHighlightNumbers(plainSegment);
    const normalizedSeconds = (secondsRaw ?? '').match(/^[0-9]+(?:\.[0-9]+)?/)?.[0] ?? '';
    if (!hideMarkers) {
      const label = normalizedSeconds ? `${normalizedSeconds}s` : '';
      const safeLabel = escapeHtml(label);
      result += `<span class="ssml-break" data-s="${safeLabel}">⏸︎${safeLabel}</span>`;
    }
    lastIndex = start + full.length;
  }
  const tail = input.slice(lastIndex);
  result += escapeAndHighlightNumbers(tail);
  return result;
};

const ChaptersScreen = observer(() => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { csAudiobookStore, errorStore, audiobookStore } = useMst() as RootInstance;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const details = id ? csAudiobookStore.detailsById.get(id) : null;
  const searchParamsString = searchParams.toString();
  const autoProcessQueryRequested = useMemo(
    () => new URLSearchParams(searchParamsString).get('autoProcess') === '1',
    [searchParamsString]
  );
  const rawChapters = id ? (csAudiobookStore.chaptersById.get(id) as CsChapter[] | undefined) : undefined;
  const chapters: CsChapter[] = useMemo(() => rawChapters ?? ([] as CsChapter[]), [rawChapters]);
  const loadingChapters = id ? csAudiobookStore.loadingByKey.get(`chapters:${id}`) : false;
  const loadingDetails = id ? !!csAudiobookStore.loadingByKey.get(`details:${id}`) : false;
  const listFallback = useMemo(() => {
    if (!id) return null;
    const items = (csAudiobookStore.itemsArray as unknown as CsAudiobookListItem[]) ?? [];
    if (!Array.isArray(items)) return null;
    return items.find((item) => String((item as { id?: unknown })?.id ?? '') === String(id)) ?? null;
  }, [csAudiobookStore.itemsArray, id]);
  const listFallbackTitle = useMemo(() => {
    if (!listFallback) return '';
    const rawTitle =
      (typeof listFallback.title === 'string' ? listFallback.title : undefined) ??
      (typeof listFallback.name === 'string' ? listFallback.name : undefined) ??
      '';
    return rawTitle.trim();
  }, [listFallback]);

  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [chapterContent, setChapterContent] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [draftContent, setDraftContent] = useState<string>('');
  const previewBoxRef = useRef<HTMLDivElement | null>(null);
  const editTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const [showBreakMarkers, setShowBreakMarkers] = useState(true);
  const lastScrollYRef = useRef(0);
  const previewScrollRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrlsMap, setAudioUrlsMap] = useState<Record<string, string | undefined>>({});
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmGenOpen, setConfirmGenOpen] = useState(false);
  const [confirmProcAllOpen, setConfirmProcAllOpen] = useState(false);
  const [confirmProcOpen, setConfirmProcOpen] = useState(false);
  const [confirmGenAllOpen, setConfirmGenAllOpen] = useState(false);
  const [autoProcessRequested, setAutoProcessRequested] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [genAllTotal, setGenAllTotal] = useState(0);
  const [runSummary, setRunSummary] = useState<{ completed: number; total: number; message?: string } | null>(null);
  const runsPollRef = useRef<number | null>(null);
  const [audioRunId, setAudioRunId] = useState<string | null>(null);
  const [audioRunDetail, setAudioRunDetail] = useState<AudioRunDetail | null>(null);
  const audioRunPollRef = useRef<number | null>(null);
  const [textRunIds, setTextRunIds] = useState<string[]>([]);
  const [textRunSummary, setTextRunSummary] = useState<{ completed: number; total: number; message?: string } | null>(
    null
  );
  const textRunsPollRef = useRef<number | null>(null);
  const [textUnitSummary, setTextUnitSummary] = useState<{ processed: number; total: number } | null>(null);
  const voicePrefLoadedRef = useRef(false);
  const [confirmExportOpen, setConfirmExportOpen] = useState(false);
  const [exportingMp3, setExportingMp3] = useState(false);
  // Local fallback for single-chapter cleaning when backend doesn't emit runs
  const [localTextProcessing, setLocalTextProcessing] = useState<Set<string>>(new Set());
  const localTextPollRef = useRef<number | null>(null);
  // Guard: suppress audio polling and auto-attachment after user cancels
  const audioCancelGuardRef = useRef<boolean>(false);
  const audioSuppressUntilRef = useRef<number>(0);
  // Local watcher for cancel-all flow to poll runs at 1–2s cadence until all stopped
  // Store-managed audio UI (single poller per audiobook). Read directly for MobX reactivity.
  const bulkChaptersSyncRef = useRef(0);
  type AudioUi = {
    completed: number;
    total: number;
    message?: string;
    cancelling?: boolean;
    running?: boolean;
    processedUnits?: number;
    totalUnits?: number;
  } | null;
  const audioUiRaw =
    id &&
    ((
      csAudiobookStore as unknown as {
        audioRunUiById?: Map<string, unknown>;
      }
    )?.audioRunUiById?.get(id) ??
      null);
  const audioUi: AudioUi =
    audioUiRaw && typeof audioUiRaw === 'object' && 'completed' in (audioUiRaw as Record<string, unknown>)
      ? (audioUiRaw as AudioUi)
      : null;
  const storePinnedRunId =
    id &&
    ((
      csAudiobookStore as unknown as {
        audioRunIdByBook?: Map<string, string>;
      }
    )?.audioRunIdByBook?.get(id) ??
      null);

  // Voice/generation controls
  const [voices, setVoices] = useState<Voice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');
  const [stability, setStability] = useState<number>(0.5);
  const [similarity, setSimilarity] = useState<number>(0.75);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const [previewAudioKey, setPreviewAudioKey] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const previewRequestIdRef = useRef(0);

  const sortedChapters = useMemo(() => {
    const copy = [...chapters];
    return copy.sort((a: CsChapter, b: CsChapter) => {
      const ao = typeof a?.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
      const bo = typeof b?.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      const at = String(a?.title ?? a?.name ?? a?.id ?? '').toLowerCase();
      const bt = String(b?.title ?? b?.name ?? b?.id ?? '').toLowerCase();
      return at.localeCompare(bt);
    });
  }, [chapters]);

  const selectedChapter = useMemo(() => {
    return sortedChapters.find((c) => String(c.id) === String(selectedChapterId));
  }, [sortedChapters, selectedChapterId]);

  const audioUrl = selectedChapter ? audioUrlsMap[String(selectedChapter.id)] || selectedChapter.audio_url : undefined;
  const bottomPlayerHeight = audioUrl ? 72 : 0;
  const stickyControlsOffset = audioUrl ? bottomPlayerHeight + 12 : 0;
  const overlayBottomOffset = 16 + bottomPlayerHeight;
  const hasCleanedText = useMemo(() => {
    const cleaned = selectedChapter?.cleaned_text;
    return !!(cleaned && cleaned.trim().length > 0);
  }, [selectedChapter]);
  const displayText = useMemo(() => {
    // Strict rule: show cleaned_text when present; do not modify content
    const cleaned = selectedChapter?.cleaned_text as string | undefined;
    if (cleaned && cleaned.trim().length > 0) {
      return cleaned;
    }
    if (chapterContent && chapterContent.trim().length > 0) {
      return chapterContent;
    }
    return '';
  }, [selectedChapter, chapterContent]);
  const displayTextHtml = useMemo(
    () => renderSsmlBreaksToHtml(displayText, { hideMarkers: !showBreakMarkers }),
    [displayText, showBreakMarkers]
  );
  const sanitizedDisplayTextHtml = useMemo(() => {
    return DOMPurify.sanitize(displayTextHtml);
  }, [displayTextHtml]);
  const textLabel = hasCleanedText ? t('csChapters.cleanedText') : t('csChapters.rawText');
  const syncScrollByRatio = useCallback((from: 'preview' | 'edit') => {
    const previewEl = previewBoxRef.current;
    const editEl = editTextAreaRef.current;
    if (!previewEl || !editEl) return;
    const map = (source: HTMLDivElement | HTMLTextAreaElement, target: HTMLDivElement | HTMLTextAreaElement) => {
      const fromMax = Math.max(source.scrollHeight - source.clientHeight, 0);
      const ratio = fromMax > 0 ? source.scrollTop / fromMax : 0;
      const toMax = Math.max(target.scrollHeight - target.clientHeight, 0);
      target.scrollTop = Math.round(ratio * toMax);
    };
    if (from === 'preview') {
      map(previewEl, editEl);
    } else {
      map(editEl, previewEl);
    }
  }, []);
  const restoreScroll = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: lastScrollYRef.current, behavior: 'auto' });
    }
  }, []);
  // Single shared audio element lives in the sticky footer; tab shows a note.

  const handleEnterEdit = useCallback(() => {
    previewScrollRef.current = previewBoxRef.current?.scrollTop ?? 0;
    lastScrollYRef.current = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
    setIsEditing(true);
    requestAnimationFrame(() => {
      restoreScroll();
      syncScrollByRatio('preview');
    });
  }, [restoreScroll, syncScrollByRatio]);

  const statusRaw = String(details?.status ?? '').toLowerCase();
  const chaptersReady = statusRaw === 'chapters_created' || statusRaw === 'completed';
  const showTocReminder = Boolean(details && !chaptersReady);
  const hasDetails = !!details;
  const showDetailSkeleton = loadingDetails && !hasDetails && listFallbackTitle.length === 0;
  const fallbackTitle =
    (details?.title && details.title.trim().length > 0 ? details.title.trim() : null) ||
    (listFallbackTitle.length > 0 ? listFallbackTitle : null) ||
    id ||
    t('csChapters.titleShort');
  const resolvedTitle = fallbackTitle;
  const detailMetaParts = useMemo(() => {
    const authorSource = hasDetails ? details?.author : listFallback?.author;
    const languageSource = hasDetails ? details?.language : listFallback?.language;
    const author = typeof authorSource === 'string' ? authorSource.trim() : '';
    const language = typeof languageSource === 'string' ? languageSource.trim() : '';
    return [author, language].filter((part) => part.length > 0);
  }, [details?.author, details?.language, hasDetails, listFallback?.author, listFallback?.language]);

  const publicAudiobookId = useMemo(() => {
    if (details?.publicAudiobookId) return details.publicAudiobookId;
    const raw =
      listFallback && typeof listFallback === 'object'
        ? (listFallback as { publicAudiobookId?: unknown; public_audiobook_id?: unknown })
        : null;
    const candidate = raw?.publicAudiobookId ?? raw?.public_audiobook_id;
    if (candidate !== undefined && candidate !== null) {
      const str = String(candidate).trim();
      if (str.length > 0) return str;
    }
    return null;
  }, [details?.publicAudiobookId, listFallback]);

  const handleExportMp3 = useCallback(async () => {
    if (!publicAudiobookId || !id) {
      errorStore.setError(t('csChapters.publicAudiobookMissing'));
      return;
    }
    setExportingMp3(true);
    try {
      await audiobookStore.getSelectedAudiobook(String(publicAudiobookId));
      const targetBook = audiobookStore.selectedAudiobook;
      if (!targetBook) {
        throw new Error('audiobook_missing');
      }
      const importResponse = await getAudiobookDetailsForImport(id);
      const importData =
        (importResponse as { data?: { chapters?: Array<{ name?: string; chapterNumber?: number; src?: string }> } })
          ?.data ?? importResponse;
      const importedChapters = (importData?.chapters ?? []).filter(
        (ch) => ch && typeof ch.src === 'string' && ch.src.length > 0
      );
      if (importedChapters.length === 0) {
        errorStore.setError('import_cs_audio_no_chapters');
        return;
      }
      const existingChapters: Chapter[] =
        targetBook.chapters?.map((ch) => ({
          ...ch,
          state: 'deleted',
        })) ?? [];
      const newChapters: Chapter[] = importedChapters.map((ch, index) => ({
        id: 0,
        name: ch.name || `Luku ${typeof ch.chapterNumber === 'number' ? ch.chapterNumber : index + 1}`,
        src: ch.src || '',
        chapterNumber:
          typeof ch.chapterNumber === 'number' && !Number.isNaN(ch.chapterNumber) ? ch.chapterNumber : index,
        state: 'new',
      }));
      const mergedChapters = [...existingChapters, ...newChapters];
      await audiobookStore.updateAudiobook(targetBook, mergedChapters, [], undefined, undefined, undefined);
      errorStore.setSuccess('csChapters.exportAudioSuccess');
    } catch (err) {
      console.error(err);
      errorStore.setError('csChapters.exportAudioFailed');
    } finally {
      setExportingMp3(false);
      setConfirmExportOpen(false);
    }
  }, [audiobookStore, errorStore, id, publicAudiobookId, t]);

  const audioRunChunkProgress = useMemo(() => {
    if (!audioRunDetail?.chapters || audioRunDetail.chapters.length === 0) return null;
    return audioRunDetail.chapters.reduce(
      (acc, chapter) => {
        const processed = Number(chapter?.processedUnits ?? 0);
        const total = Number(chapter?.totalUnits ?? 0);
        if (Number.isFinite(processed)) acc.processed += Math.max(processed, 0);
        if (Number.isFinite(total)) acc.total += Math.max(total, 0);
        return acc;
      },
      { processed: 0, total: 0 }
    );
  }, [audioRunDetail?.chapters]);

  const audioProgress = useMemo(() => {
    if (audioRunDetail) {
      const total =
        Number(audioRunDetail.totalChapters ?? 0) > 0
          ? Number(audioRunDetail.totalChapters ?? 0)
          : Number(audioRunDetail.chapters?.length ?? 0);
      const completed = Number(audioRunDetail.completedChapters ?? 0);
      const failed = Number(audioRunDetail.failedChapters ?? 0);
      const done = completed + failed;
      const safeTotal = total > 0 ? total : Math.max(done, 1);
      return {
        source: 'run' as const,
        completed: Math.min(done, safeTotal),
        total: safeTotal,
        chunkProcessed: audioRunChunkProgress?.processed ?? undefined,
        chunkTotal: audioRunChunkProgress?.total ?? undefined,
        status: audioRunDetail.status,
      };
    }
    if (audioUi?.running) {
      return {
        source: 'store' as const,
        completed: audioUi.completed,
        total: audioUi.total || 1,
        chunkProcessed: audioUi.processedUnits,
        chunkTotal: audioUi.totalUnits,
        status: audioUi.running ? 'running' : undefined,
      };
    }
    if (runSummary && audioRunId) {
      return {
        source: 'legacy' as const,
        completed: runSummary.completed,
        total: runSummary.total || 1,
        chunkProcessed: undefined,
        chunkTotal: undefined,
        status: undefined,
      };
    }
    return null;
  }, [audioRunDetail, audioRunChunkProgress, audioUi, runSummary, audioRunId]);

  const previewingVoice = useMemo(() => {
    if (!previewingVoiceId) return null;
    return voices.find((v) => String(v.voice_id) === String(previewingVoiceId)) ?? null;
  }, [voices, previewingVoiceId]);
  const selectedVoiceLabel = useMemo(() => {
    if (!selectedVoice) return t('csChapters.defaultVoice');
    const match = voices.find((v) => String(v.voice_id) === String(selectedVoice));
    return match?.name ?? t('csChapters.defaultVoice');
  }, [selectedVoice, voices, t]);
  const renderVoiceList = (maxHeight = 220) => (
    <>
      <List
        dense
        sx={{
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: 1,
          maxHeight,
          overflowY: maxHeight ? 'auto' : 'visible',
          mt: 0.5,
        }}
      >
        {voices.map((v) => (
          <ListItem
            key={v.voice_id}
            secondaryAction={
              previewLoading && previewingVoiceId === v.voice_id ? (
                <CircularProgress size={18} />
              ) : (
                <Tooltip title={t('csChapters.previewVoice')}>
                  <span>
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => onPreviewVoice(v.voice_id)}
                      aria-label={t('csChapters.previewVoice')}
                      disabled={previewLoading && previewingVoiceId !== v.voice_id}
                    >
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            }
          >
            <ListItemText
              primary={v.name}
              secondary={previewingVoiceId === v.voice_id ? t('csChapters.previewingNow') : undefined}
            />
          </ListItem>
        ))}
        {voices.length === 0 && (
          <ListItem>
            <ListItemText primary={<Typography variant="body2">{t('csChapters.noVoices')}</Typography>} />
          </ListItem>
        )}
      </List>
      {previewSrc && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="GrayText" sx={{ display: 'block', mb: 0.5 }}>
            {t('csChapters.previewingVoice', {
              name: previewingVoice?.name ?? previewingVoiceId ?? t('csChapters.defaultVoice'),
            })}
          </Typography>
          <audio
            key={`${previewAudioKey}-${previewingVoiceId ?? 'none'}`}
            controls
            autoPlay
            style={{
              width: '100%',
            }}
            crossOrigin="anonymous"
            src={previewSrc}
          />
        </Box>
      )}
      {previewError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {previewError}
        </Typography>
      )}
    </>
  );

  useEffect(() => {
    if (storePinnedRunId && storePinnedRunId !== audioRunId) {
      setAudioRunId(storePinnedRunId);
    }
  }, [storePinnedRunId, audioRunId]);

  const onPreviewVoice = useCallback(
    async (voiceId: string) => {
      if (!voiceId) return;
      const requestId = previewRequestIdRef.current + 1;
      previewRequestIdRef.current = requestId;
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
      setPreviewingVoiceId(voiceId);
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await getVoicePreview('elevenlabs', voiceId);
        const blob = (res.data ?? res) as Blob;
        const objectUrl = URL.createObjectURL(blob);
        if (previewRequestIdRef.current !== requestId) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        previewObjectUrlRef.current = objectUrl;
        setPreviewSrc(objectUrl);
        setPreviewAudioKey((prev) => prev + 1);
      } catch (err) {
        if (previewRequestIdRef.current === requestId) {
          setPreviewError(t('csChapters.previewFailed'));
          setPreviewSrc(null);
        }
      } finally {
        if (previewRequestIdRef.current === requestId) {
          setPreviewLoading(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
      }
    };
  }, []);

  const syncChaptersThrottled = useCallback(
    async (force?: boolean) => {
      if (!id) return;
      const now = Date.now();
      if (!force && now - bulkChaptersSyncRef.current < 4000) return;
      bulkChaptersSyncRef.current = now;
      try {
        await csAudiobookStore.listChapters(id);
      } catch (_) {
        // ignore refresh errors
      }
    },
    [id, csAudiobookStore]
  );

  const detectActiveAudioRun = useCallback(async () => {
    if (!id) return null;
    try {
      const payload = (await csAudiobookStore.listRuns(id, 'audio_generation_batch')) as
        | Array<{ id?: string; status?: string; startedAt?: string }>
        | { items?: Array<{ id?: string; status?: string; startedAt?: string }> }
        | null;
      const items = Array.isArray(payload)
        ? payload
        : (((payload as { items?: Array<{ id?: string; status?: string; startedAt?: string }> })?.items ??
            []) as Array<{ id?: string; status?: string; startedAt?: string }>);
      if (!Array.isArray(items) || items.length === 0) return null;
      const active = items
        .filter((run) => isRunActiveStatus(String(run?.status ?? '')))
        .sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || ''));
      if (active.length === 0) {
        return null;
      }
      const candidate = active[0];
      const runId = candidate?.id ? String(candidate.id) : null;
      if (runId) {
        setAudioRunId(runId);
      }
      return runId;
    } catch (_) {
      return null;
    }
  }, [id, csAudiobookStore]);

  useEffect(() => {
    // Sync draft with current display when changing selection
    setDraftContent(displayText);
  }, [displayText, selectedChapterId]);

  useEffect(() => {
    if (!id) return;
    csAudiobookStore.getDetails(id);
    csAudiobookStore.listChapters(id);
    if (!audioRunId) {
      void detectActiveAudioRun();
    }
  }, [id, csAudiobookStore, audioRunId, detectActiveAudioRun]);

  useEffect(() => {
    if (sortedChapters.length > 0 && !selectedChapterId) {
      setSelectedChapterId(String(sortedChapters[0].id));
    }
  }, [sortedChapters, selectedChapterId]);

  useEffect(() => {
    if (!autoProcessQueryRequested) return;
    setAutoProcessRequested(true);
    const next = new URLSearchParams(searchParamsString);
    next.delete('autoProcess');
    setSearchParams(next, { replace: true });
  }, [autoProcessQueryRequested, searchParamsString, setSearchParams]);

  // Load voices (ElevenLabs)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await listVoicesElevenLabs();
        const arr = (res.data ?? []) as Voice[];
        if (mounted) setVoices(arr);
      } catch (_) {
        if (mounted) setVoices([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    const key = `cs_voice_pref_${id}`;
    let stored = '';
    try {
      if (typeof window !== 'undefined') {
        stored = window.localStorage.getItem(key) ?? '';
      }
    } catch (_) {
      stored = '';
    }
    setSelectedVoice(stored);
    voicePrefLoadedRef.current = true;
    return () => {
      voicePrefLoadedRef.current = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id || !voicePrefLoadedRef.current) return;
    const key = `cs_voice_pref_${id}`;
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, selectedVoice ?? '');
      }
    } catch (_) {
      // ignore persistence failures
    }
  }, [id, selectedVoice]);

  // No background polling; refresh only on demand

  // Load audio URLs when switching to Audio tab
  useEffect(() => {
    const loadAudio = async () => {
      if (!id || activeTab !== 1) return;
      const data = await csAudiobookStore.listAudioUrls(id);
      const map: Record<string, string | undefined> = {};
      const items = (data?.chapters ?? []) as Array<{ chapter_id: string; audio_url?: string }>;
      items.forEach((it) => {
        map[String(it.chapter_id)] = it.audio_url;
      });
      setAudioUrlsMap(map);
    };
    loadAudio();
  }, [activeTab, id, csAudiobookStore]);

  const refresh = useCallback(async () => {
    if (!id) return;
    await csAudiobookStore.listChapters(id);
    if (activeTab === 0 && selectedChapterId) {
      try {
        const text = await csAudiobookStore.getChapterContent(id, selectedChapterId);
        setChapterContent(text as string);
      } catch (_) {
        // ignore content load errors in refresh
      }
    }
    if (activeTab === 1) {
      const data = await csAudiobookStore.listAudioUrls(id);
      const map: Record<string, string | undefined> = {};
      const items = (data?.chapters ?? []) as Array<{ chapter_id: string; audio_url?: string }>;
      items.forEach((it) => {
        map[String(it.chapter_id)] = it.audio_url;
      });
      setAudioUrlsMap(map);
    }
  }, [id, csAudiobookStore, activeTab, selectedChapterId]);

  // Keep a ref to the auto-promotion callback to avoid order/deps issues
  const maybePromoteIfAllAudioReadyRef = useRef<(() => Promise<void>) | null>(null);

  // Poll progress runs is handled by store; ensure component-local timers are off
  useEffect(() => {
    if (runsPollRef.current) {
      window.clearInterval(runsPollRef.current);
      runsPollRef.current = null;
    }
    setRunSummary(null);
    return () => {
      if (runsPollRef.current) {
        window.clearInterval(runsPollRef.current);
        runsPollRef.current = null;
      }
    };
  }, [id]);

  useEffect(() => {
    if (!id || !audioRunId) {
      setAudioRunDetail(null);
      if (audioRunPollRef.current) {
        window.clearTimeout(audioRunPollRef.current);
        audioRunPollRef.current = null;
      }
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || !id || !audioRunId) return;
      try {
        const detail = (await csAudiobookStore.getRun(id, audioRunId)) as AudioRunDetail | null;
        if (cancelled) return;
        if (detail) {
          setAudioRunDetail(detail);
          if (!isRunActiveStatus(detail.status)) {
            setTimeout(() => {
              if (maybePromoteIfAllAudioReadyRef.current) {
                void maybePromoteIfAllAudioReadyRef.current();
              }
            }, 0);
            window.setTimeout(() => {
              setAudioRunId((prev) => (prev === audioRunId ? null : prev));
            }, 3000);
            return;
          }
        }
      } catch (_) {
        // ignore polling errors
      }
      audioRunPollRef.current = window.setTimeout(poll, 1800);
    };
    poll();
    return () => {
      cancelled = true;
      if (audioRunPollRef.current) {
        window.clearTimeout(audioRunPollRef.current);
        audioRunPollRef.current = null;
      }
    };
  }, [audioRunId, id, csAudiobookStore]);

  // Stop store poller on unmount or audiobook change
  useEffect(() => {
    return () => {
      if (id) {
        const anyStore = csAudiobookStore as unknown as { stopAudioRunsPolling?: (aid: string) => void };
        anyStore?.stopAudioRunsPolling?.(id);
      }
    };
  }, [id, csAudiobookStore]);

  // Poll text cleaning runs
  useEffect(() => {
    if (!id) return;
    if (textRunIds.length === 0) {
      if (textRunsPollRef.current) {
        window.clearInterval(textRunsPollRef.current);
        textRunsPollRef.current = null;
      }
      setTextRunSummary(null);
      return;
    }
    if (textRunsPollRef.current) return;
    textRunsPollRef.current = window.setInterval(
      async () => {
        try {
          type TextRun = {
            id?: unknown;
            status?: unknown;
            totalChapters?: unknown;
            completedChapters?: unknown;
            failedChapters?: unknown;
            lastMessage?: unknown;
          };
          const api = csAudiobookStore as unknown as {
            listRuns: (id: string, type?: 'text_cleaning') => Promise<unknown>;
          };
          const payload = (await api.listRuns(id, 'text_cleaning')) as unknown;
          const itemsUnknown = Array.isArray(payload)
            ? (payload as unknown[])
            : (payload as { items?: unknown[] })?.items;
          const runsArr: TextRun[] = Array.isArray(itemsUnknown) ? (itemsUnknown as TextRun[]) : [];
          const relevant = runsArr.filter((r) => textRunIds.includes(String((r as { id?: unknown }).id ?? '')));
          if (relevant.length === 0) {
            setTextRunIds([]);
            setTextUnitSummary(null);
            return;
          }
          const total = relevant.reduce((acc: number, r) => {
            const n = Number((r as { totalChapters?: unknown }).totalChapters ?? 0);
            return acc + (Number.isFinite(n) ? n : 0);
          }, 0);
          const completed = relevant.reduce((acc: number, r) => {
            const c = Number((r as { completedChapters?: unknown }).completedChapters ?? 0);
            const f = Number((r as { failedChapters?: unknown }).failedChapters ?? 0);
            return acc + (Number.isFinite(c) ? c : 0) + (Number.isFinite(f) ? f : 0);
          }, 0);
          const running = relevant.find((r) => String((r as { status?: unknown }).status ?? '') === 'running');
          const message =
            running && typeof (running as { lastMessage?: unknown }).lastMessage === 'string'
              ? String((running as { lastMessage?: unknown }).lastMessage)
              : undefined;
          setTextRunSummary({ completed, total, message });

          // Additionally compute chunk-level units across all relevant runs
          try {
            const api2 = csAudiobookStore as unknown as {
              getRun?: (
                aid: string,
                runId: string
              ) => Promise<{ chapters?: Array<{ processedUnits?: number; totalUnits?: number }> } | null>;
            };
            if (api2?.getRun) {
              let processedSum = 0;
              let totalSum = 0;
              // Map relevant to their IDs (we need the ids)
              const ids: string[] = relevant
                .map((r) => String((r as { id?: unknown }).id ?? ''))
                .filter((s) => s.length > 0);
              for (const rid of ids) {
                const res = await api2.getRun(id, rid);
                const chaptersUnits = (res?.chapters as Array<{ processedUnits?: number; totalUnits?: number }>) ?? [];
                for (const cu of chaptersUnits) {
                  const pu = Number(cu.processedUnits ?? 0);
                  const tu = Number(cu.totalUnits ?? 0);
                  if (Number.isFinite(pu)) processedSum += pu;
                  if (Number.isFinite(tu)) totalSum += tu;
                }
              }
              setTextUnitSummary({ processed: processedSum, total: totalSum });
            }
          } catch (_) {
            // silently ignore unit aggregation errors
          }

          const allDone = relevant.every((r) => String((r as { status?: unknown }).status ?? '') !== 'running');
          if (allDone) {
            setTextRunIds([]);
            setTextUnitSummary(null);
            await refresh();
          }
        } catch (_) {
          // ignore poll errors
        }
      },
      5000 + Math.floor(Math.random() * 1000)
    );
    return () => {
      if (textRunsPollRef.current) {
        window.clearInterval(textRunsPollRef.current);
        textRunsPollRef.current = null;
      }
    };
  }, [textRunIds, id, csAudiobookStore, refresh]);

  // Local fallback poller for single-chapter cleaning (no runs)
  useEffect(() => {
    if (!id) return;
    if (localTextProcessing.size === 0) {
      if (localTextPollRef.current) {
        window.clearInterval(localTextPollRef.current);
        localTextPollRef.current = null;
      }
      return;
    }
    if (localTextPollRef.current) return;
    localTextPollRef.current = window.setInterval(
      async () => {
        try {
          await csAudiobookStore.listChapters(id);
          const latest = (csAudiobookStore.chaptersById.get(id) as CsChapter[] | undefined) ?? [];
          setLocalTextProcessing((prev) => {
            const next = new Set<string>();
            prev.forEach((cid) => {
              const ch = latest.find((c) => String(c.id) === String(cid));
              const st = String(ch?.status ?? '').toLowerCase();
              if (st === 'processing') {
                next.add(cid);
              }
            });
            if (next.size === 0 && localTextPollRef.current) {
              window.clearInterval(localTextPollRef.current);
              localTextPollRef.current = null;
            }
            return next;
          });
        } catch (_) {
          // ignore
        }
      },
      4000 + Math.floor(Math.random() * 1000)
    );
    return () => {
      if (localTextPollRef.current) {
        window.clearInterval(localTextPollRef.current);
        localTextPollRef.current = null;
      }
    };
  }, [localTextProcessing.size, id, csAudiobookStore]);

  const maybePromoteIfAllAudioReady = useCallback(async () => {
    if (!id) return;
    // Fetch latest chapters and audio urls
    const list = (await csAudiobookStore.listChapters(id)) as unknown as CsChapter[] | undefined;
    const chaptersLatest: CsChapter[] = Array.isArray(list)
      ? (list as CsChapter[])
      : ((csAudiobookStore.chaptersById.get(id) as CsChapter[] | undefined) ?? []);
    if (!chaptersLatest || chaptersLatest.length === 0) return;
    const urls = await csAudiobookStore.listAudioUrls(id);
    const items = (urls?.chapters ?? []) as Array<{ chapter_id: string; audio_url?: string }>;
    const urlMap = new Map<string, string | undefined>();
    for (const it of items) {
      urlMap.set(String(it.chapter_id), it.audio_url);
    }
    const allHaveAudio = chaptersLatest.every((ch) => {
      const st = String(ch?.status ?? '').toLowerCase();
      if (st === 'completed') return true;
      type ChapterWithOptionalAudio = CsChapter & { audio_url?: string };
      const url = urlMap.get(String(ch.id)) || (ch as ChapterWithOptionalAudio)?.audio_url;
      return !!(url && String(url).length > 0);
    });
    if (allHaveAudio) {
      try {
        await csAudiobookStore.setStatus(id, 'completed');
        // Refresh details banner
        await csAudiobookStore.getDetails(id);
      } catch (_) {
        // ignore
      }
    }
  }, [id, csAudiobookStore]);

  useEffect(() => {
    maybePromoteIfAllAudioReadyRef.current = maybePromoteIfAllAudioReady;
  }, [maybePromoteIfAllAudioReady]);

  const loadContent = useCallback(
    async (chapterId: string) => {
      if (!id) return;
      const text = await csAudiobookStore.getChapterContent(id, chapterId);
      setChapterContent(text);
    },
    [csAudiobookStore, id]
  );

  const handleTextSave = useCallback(async () => {
    if (!id || !selectedChapter) return;
    previewScrollRef.current = editTextAreaRef.current?.scrollTop ?? previewScrollRef.current;
    setActionBusy(true);
    try {
      await csAudiobookStore.updateChapterContent(id, String(selectedChapter.id), draftContent, {
        updateRaw: false,
      });
      setIsEditing(false);
      requestAnimationFrame(() => {
        syncScrollByRatio('edit');
      });
      await loadContent(String(selectedChapter.id));
      await csAudiobookStore.listChapters(id);
    } finally {
      setActionBusy(false);
    }
  }, [csAudiobookStore, draftContent, id, loadContent, selectedChapter, syncScrollByRatio]);

  const handleCancelEditing = useCallback(() => {
    previewScrollRef.current = editTextAreaRef.current?.scrollTop ?? previewScrollRef.current;
    lastScrollYRef.current = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
    setDraftContent(displayText);
    setIsEditing(false);
    requestAnimationFrame(() => {
      restoreScroll();
      syncScrollByRatio('edit');
    });
  }, [displayText, restoreScroll, syncScrollByRatio]);

  useEffect(() => {
    if (selectedChapterId) {
      loadContent(selectedChapterId);
    }
  }, [selectedChapterId, loadContent]);

  const onProcess = async () => {
    if (!id || !selectedChapter) return;
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const data = (await csAudiobookStore.processChapter(id, String(selectedChapter.id))) as { runId?: string } | null;
      let runIdCaptured = false;
      if (data?.runId) {
        setTextRunIds((prev) => Array.from(new Set([...prev, String(data.runId)])));
        runIdCaptured = true;
      } else {
        // Fallback: find a running or most recent text_cleaning run
        try {
          const api = csAudiobookStore as unknown as {
            listRuns?: (
              aid: string,
              type?: 'text_cleaning'
            ) => Promise<
              | { items?: Array<{ id?: string; status?: string; startedAt?: string }> }
              | Array<{ id?: string; status?: string; startedAt?: string }>
              | null
            >;
          };
          if (api?.listRuns) {
            const payload = await api.listRuns(id, 'text_cleaning');
            const items = Array.isArray(payload)
              ? (payload as Array<{ id?: string; status?: string; startedAt?: string }>)
              : ((payload?.items as Array<{ id?: string; status?: string; startedAt?: string }>) ?? []);
            let chosen = items.find((r) => String(r.status || '').toLowerCase() === 'running');
            if (!chosen && items.length > 0) {
              chosen = items.slice().sort((a, b) => Date.parse(b.startedAt || '') - Date.parse(a.startedAt || ''))[0];
            }
            const fallbackRunId = chosen?.id ? String(chosen.id) : null;
            if (fallbackRunId) {
              setTextRunIds((prev) => Array.from(new Set([...prev, fallbackRunId])));
              runIdCaptured = true;
            }
          }
        } catch (_) {
          // ignore fallback errors
        }
        // If still no run, mark this chapter as locally processing and start a lightweight poll
        if (!runIdCaptured) {
          setLocalTextProcessing((prev) => {
            const next = new Set(prev);
            next.add(String(selectedChapter.id));
            return next;
          });
        }
      }
      await refresh();
      if (selectedChapterId) {
        await loadContent(selectedChapterId);
      }
    } finally {
      setActionBusy(false);
    }
  };

  const onGenerate = async () => {
    if (!id || !selectedChapter) return;
    if (actionBusy) return;
    setActionBusy(true);
    // Clear any previous cancel guard (user started a fresh generation)
    audioCancelGuardRef.current = false;
    audioSuppressUntilRef.current = 0;
    // Optimistically prime store UI and start store-level poller
    try {
      const anyStore = csAudiobookStore as unknown as {
        primeAudioUi?: (aid: string, message?: string) => void;
        startAudioRunsPolling?: (aid: string) => Promise<void> | void;
      };
      anyStore?.primeAudioUi?.(id, 'Aloitetaan äänen luonti…');
      await anyStore?.startAudioRunsPolling?.(id);
    } catch {
      // ignore
    }
    try {
      // Refresh latest chapter status to avoid acting on stale state
      try {
        await csAudiobookStore.listChapters(id);
      } catch (_) {
        // ignore refresh errors
      }
      const latestList = (csAudiobookStore.chaptersById.get(id) as CsChapter[] | undefined) ?? [];
      const latest = latestList.find((c) => String(c.id) === String(selectedChapter.id));
      const latestStatus = String(latest?.status ?? selectedChapter.status ?? '').toLowerCase();
      if (latestStatus === 'audio_processing') {
        // Try resetting to approved; if cannot cancel, attach existing run (if any) and bail
        type ResetResult = { ok: boolean; status?: number; error?: string; data?: unknown };
        try {
          const r = (await csAudiobookStore.resetChapterAudio(id, String(selectedChapter.id), {
            target_status: 'approved',
            force: true,
          })) as ResetResult;
          const cannotCancel = r && r.ok === false && (r.error === 'cannot_cancel' || r.status === 409);
          if (cannotCancel) {
            return;
          }
        } catch (_) {
          // If reset failed unexpectedly, continue and let backend enforce
        }
      }
      // Approve only if in review; skip approval if already approved/completed to avoid 500
      const currentStatus = String(selectedChapter.status ?? '').toLowerCase();
      if (currentStatus === 'review') {
        await csAudiobookStore.approveChapter(id, String(selectedChapter.id), selectedVoice || undefined);
      }
      await csAudiobookStore.generateChapterAudio(id, String(selectedChapter.id), {
        useOpenai: false,
        voiceId: selectedVoice || undefined,
        stability,
        similarityBoost: similarity,
      });
      void detectActiveAudioRun();
      await refresh();
    } finally {
      setActionBusy(false);
    }
  };

  const onProcessAll = useCallback(async () => {
    if (!id || actionBusy) return;
    setActionBusy(true);
    try {
      const data = (await csAudiobookStore.processAll(id)) as unknown as { runId?: string } | null;
      if (data?.runId) {
        setTextRunIds((prev) => Array.from(new Set([...prev, String(data.runId)])));
      }
      await refresh();
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, csAudiobookStore, id, refresh]);

  useEffect(() => {
    if (!autoProcessRequested) return;
    if (!id || loadingChapters) return;
    if (actionBusy) return;
    if (sortedChapters.length === 0) return;
    if (textRunIds.length > 0 || localTextProcessing.size > 0) return;
    setAutoProcessRequested(false);
    void onProcessAll();
  }, [
    actionBusy,
    autoProcessRequested,
    id,
    loadingChapters,
    localTextProcessing,
    onProcessAll,
    sortedChapters.length,
    textRunIds.length,
  ]);

  const onGenerateAll = async () => {
    if (!id || generatingAll) return;
    await syncChaptersThrottled(true);
    void detectActiveAudioRun();
    const eligibleChapters: string[] = sortedChapters
      .map((ch) => {
        const titleLower = String(ch?.title ?? ch?.name ?? '')
          .trim()
          .toLowerCase();
        if (titleLower === 'complete book') return null;
        return String(ch.id);
      })
      .filter((chapterId): chapterId is string => !!chapterId && chapterId.length > 0);
    if (eligibleChapters.length === 0) {
      errorStore?.setError?.('Yhtään lukua ei voitu valmistella. Tarkista luvut.');
      return;
    }
    setGeneratingAll(true);
    setGenAllTotal(eligibleChapters.length);
    try {
      const data = (await (
        csAudiobookStore as unknown as {
          generateAudioBatch?: (
            aid: string,
            body: {
              useOpenai?: boolean;
              voiceId?: string;
              stability?: number;
              similarityBoost?: number;
              chapterIds?: string[];
            }
          ) => Promise<{ runId?: string } | undefined>;
        }
      ).generateAudioBatch?.(id, {
        useOpenai: false,
        voiceId: selectedVoice || undefined,
        stability,
        similarityBoost: similarity,
        chapterIds: eligibleChapters,
      })) as { runId?: string } | undefined;
      const runId = data?.runId || 'run_audio_generation_batch';
      setAudioRunId(runId);
      void detectActiveAudioRun();
    } finally {
      setGeneratingAll(false);
    }
  };

  // Cancel helpers
  const onCancelAllAudio = async () => {
    if (!id) return;
    try {
      const api = csAudiobookStore as unknown as {
        cancelAllRuns?: (aid: string, type?: 'audio_generation') => Promise<{ ok: boolean }>;
        setAudioRunUi?: (aid: string, ui: null) => void;
      };
      await api?.cancelAllRuns?.(id, 'audio_generation');
      api?.setAudioRunUi?.(id, null);
    } finally {
      setAudioRunId(null);
      setAudioRunDetail(null);
    }
  };

  const onCancelSelectedChapterAudio = async () => {
    if (!id || !selectedChapter) return;
    try {
      // Stop UI/polling immediately for a clean UX; suppression window to avoid re-attachment
      audioCancelGuardRef.current = true;
      audioSuppressUntilRef.current = Date.now() + 8000;
      if (runsPollRef.current) {
        window.clearInterval(runsPollRef.current);
        runsPollRef.current = null;
      }
      setRunSummary(null);
      // Find running run that includes this chapter
      const api = csAudiobookStore as unknown as {
        listRuns?: (
          aid: string,
          type?: 'audio_generation'
        ) => Promise<
          | { items?: Array<{ id?: string; status?: string; startedAt?: string }> }
          | Array<{ id?: string; status?: string; startedAt?: string }>
          | null
        >;
        getRun?: (aid: string, runId: string) => Promise<{ chapters?: Array<{ chapterId?: string }> } | null>;
        cancelRun?: (aid: string, runId: string) => Promise<{ ok: boolean }>;
      };
      let targetRunId: string | null = null;
      if (api?.listRuns) {
        const payload = await api.listRuns(id, 'audio_generation');
        const items = Array.isArray(payload)
          ? (payload as Array<{ id?: string; status?: string; startedAt?: string }>)
          : ((payload?.items as Array<{ id?: string; status?: string; startedAt?: string }>) ?? []);
        const running = items.filter((r) => String(r.status || '').toLowerCase() === 'running');
        if (api?.getRun) {
          for (const r of running) {
            const rid = String(r.id || '');
            if (!rid) continue;
            const detail = await api.getRun(id, rid);
            const chapters = (detail?.chapters as Array<{ chapterId?: string }>) ?? [];
            const match = chapters.find((c) => String(c.chapterId || '') === String(selectedChapter.id));
            if (match) {
              targetRunId = rid;
              break;
            }
          }
        }
        if (!targetRunId && running.length > 0) {
          // Fallback: cancel the first running run
          targetRunId = String(running[0].id || '');
        }
      }
      if (targetRunId && api?.cancelRun) {
        await api.cancelRun(id, targetRunId);
      }
    } finally {
      // No automatic follow-up; user will refresh manually if needed
    }
  };

  // Removed Full Pipeline action; not supported

  return (
    <>
      <Layout title={resolvedTitle}>
        <Grid container spacing={2}>
          {showTocReminder && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="GrayText">
                  {t('csChapters.needToc')}
                </Typography>
                <MuiButton size="small" variant="outlined" onClick={() => id && navigate(`/cs-audiobooks/${id}/toc`)}>
                  {t('csChapters.gotoToc')}
                </MuiButton>
              </Box>
            </Grid>
          )}
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid item>
                {showDetailSkeleton ? (
                  <>
                    <Skeleton variant="text" width={220} height={32} />
                    <Skeleton variant="text" width={160} height={20} />
                  </>
                ) : (
                  <>
                    <Typography variant="h6">{resolvedTitle}</Typography>
                    {detailMetaParts.length > 0 && (
                      <Typography variant="caption" color="GrayText">
                        {detailMetaParts.join(' • ')}
                      </Typography>
                    )}
                  </>
                )}
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                  <MuiButton
                    variant="outlined"
                    color="primary"
                    onClick={() => setConfirmGenAllOpen(true)}
                    disabled={actionBusy || generatingAll}
                    sx={{ minWidth: { sm: 220 } }}
                  >
                    {t('csChapters.generateAll')}
                  </MuiButton>
                  <MuiButton
                    variant="contained"
                    color="success"
                    onClick={() => setConfirmExportOpen(true)}
                    disabled={!publicAudiobookId || exportingMp3}
                    sx={{ fontWeight: 700, minWidth: { sm: 220 } }}
                  >
                    {t('csChapters.exportAudioButton')}
                  </MuiButton>
                </Box>
                {/* Full Pipeline removed */}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle2">{t('csChapters.titleShort')}</Typography>
                <IconButton onClick={refresh} size="small" disabled={actionBusy}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              <MuiButton
                size="small"
                variant="outlined"
                onClick={() => setConfirmProcAllOpen(true)}
                disabled={actionBusy}
                sx={{ flexShrink: 0 }}
              >
                {t('csChapters.processAll')}
              </MuiButton>
            </Box>
            {loadingChapters ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2">{t('csChapters.loading')}</Typography>
              </Box>
            ) : (
              <List dense>
                {sortedChapters.map((ch: CsChapter) => (
                  <ListItem
                    key={String(ch.id)}
                    disableGutters
                    secondaryAction={statusChipForChapter(ch, audioUrlsMap[String(ch.id)] || ch.audio_url)}
                  >
                    <ListItemButton
                      selected={String(ch.id) === String(selectedChapterId)}
                      onClick={() => setSelectedChapterId(String(ch.id))}
                    >
                      <ListItemText
                        primary={ch.title ?? ch.name ?? `Chapter ${ch.order ?? ''}`}
                        secondary={ch.duration ? `${Math.round(ch.duration)}s` : ''}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                {chapters.length === 0 && (
                  <ListItem>
                    <ListItemText primary={<Typography color="GrayText">{t('csChapters.noChapters')}</Typography>} />
                  </ListItem>
                )}
              </List>
            )}

            {/* Manual refresh only; auto-refresh removed for better UX */}
          </Grid>

          <Grid item xs={12} md={9}>
            {selectedChapter ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {selectedChapter.title ?? selectedChapter.name ?? `Chapter ${selectedChapter.order ?? ''}`}
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab label={t('csChapters.textTab')} />
                    <Tab label={t('csChapters.audioTab')} />
                  </Tabs>
                </Box>
                <Box hidden={activeTab !== 0} sx={{ py: 2 }}>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexGrow: 1 }}>
                      <MuiButton
                        variant="outlined"
                        size="small"
                        onClick={() => setConfirmProcOpen(true)}
                        disabled={actionBusy || String((selectedChapter.status ?? '').toLowerCase()) === 'processing'}
                      >
                        {t('csChapters.process')}
                      </MuiButton>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={showBreakMarkers}
                            onChange={(e) => setShowBreakMarkers(e.target.checked)}
                          />
                        }
                        label={t('csChapters.showBreakMarkers', { defaultValue: 'Näytä taukomerkit' })}
                        sx={{ ml: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isEditing && (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={t('csChapters.editingMode')}
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {isEditing ? (
                    <>
                      <TextField
                        label={textLabel}
                        value={draftContent}
                        onChange={(e) => setDraftContent(e.target.value)}
                        fullWidth
                        minRows={12}
                        multiline
                        inputRef={editTextAreaRef}
                        InputProps={{
                          sx: {
                            '& textarea': {
                              fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                              fontSize: '0.95rem',
                              lineHeight: 1.6,
                              padding: '10px 12px',
                              resize: 'none',
                            },
                          },
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {textLabel}
                      </Typography>
                      <Box
                        ref={previewBoxRef}
                        sx={{
                          border: '1px solid rgba(0,0,0,0.23)',
                          borderRadius: 1,
                          px: 1.5,
                          py: 1.25,
                          minHeight: '12em',
                          whiteSpace: 'pre-wrap',
                          bgcolor: 'rgba(0,0,0,0.02)',
                          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          color: 'rgba(0,0,0,0.87)',
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizedDisplayTextHtml }}
                      />
                    </>
                  )}
                  <Box
                    sx={{
                      position: 'sticky',
                      bottom: stickyControlsOffset,
                      mt: 2,
                      py: 1,
                      px: 1,
                      display: 'flex',
                      gap: 1,
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      bgcolor: 'rgba(255,255,255,0.9)',
                      backdropFilter: 'blur(4px)',
                      borderTop: '1px solid rgba(0,0,0,0.08)',
                      zIndex: 2,
                    }}
                  >
                    {isEditing && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={t('csChapters.editingMode')}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {isEditing ? (
                      <>
                        <MuiButton variant="contained" size="small" disabled={actionBusy} onClick={handleTextSave}>
                          {t('csChapters.save')}
                        </MuiButton>
                        <MuiButton size="small" onClick={handleCancelEditing}>
                          {t('csChapters.cancel')}
                        </MuiButton>
                      </>
                    ) : (
                      <MuiButton size="small" variant="outlined" onClick={handleEnterEdit}>
                        {t('csChapters.edit')}
                      </MuiButton>
                    )}
                  </Box>
                </Box>
                <Box hidden={activeTab !== 1} sx={{ py: 2 }}>
                  {audioUrl ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('csChapters.audioControlsInfo', {
                        defaultValue: 'Äänentoisto ja säätimet löytyvät alareunan soittimesta.',
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="GrayText">
                      {t('csChapters.noAudio')}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <MuiButton
                        variant="contained"
                        onClick={() => setConfirmGenOpen(true)}
                        disabled={actionBusy || !hasCleanedText}
                      >
                        {t('csChapters.generate')}
                      </MuiButton>
                    </Grid>
                    {String((selectedChapter.status ?? '').toLowerCase()) === 'audio_processing' && (
                      <Grid item>
                        <MuiButton
                          variant="outlined"
                          color="error"
                          onClick={onCancelSelectedChapterAudio}
                          disabled={actionBusy}
                        >
                          Peruuta
                        </MuiButton>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="GrayText">
                        {t('csChapters.generateInfo')}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={5}>
                      <Select
                        size="small"
                        value={selectedVoice}
                        displayEmpty
                        fullWidth
                        onChange={(e) => setSelectedVoice(String(e.target.value))}
                      >
                        <MenuItem value="">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                            }}
                          >
                            <em>{t('csChapters.defaultVoice')}</em>
                          </Box>
                        </MenuItem>
                        {voices.map((v) => (
                          <MenuItem key={v.voice_id} value={v.voice_id}>
                            <Typography variant="body2">{v.name}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption">{t('csChapters.stability')}</Typography>
                      <Slider
                        value={stability}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(_, v) => setStability(v as number)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption">{t('csChapters.similarity')}</Typography>
                      <Slider
                        value={similarity}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(_, v) => setSimilarity(v as number)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="GrayText">
                        {t('csChapters.previewVoiceList')}
                      </Typography>
                      {renderVoiceList()}
                    </Grid>
                  </Grid>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="GrayText">
                {t('csChapters.selectChapter')}
              </Typography>
            )}
          </Grid>
        </Grid>
        {/* Spacer to avoid bottom player overlaying content */}
        <Box sx={{ height: 72 }} />
      </Layout>
      {(audioProgress || textRunSummary || localTextProcessing.size > 0) && (
        <Box sx={{ position: 'fixed', left: 16, right: 16, bottom: overlayBottomOffset, zIndex: 1300 }}>
          {(textRunSummary || localTextProcessing.size > 0) && (
            <Box
              sx={{
                mb: 1,
                p: 1,
                border: '1px solid rgba(0,0,0,0.16)',
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 2,
              }}
            >
              <Typography variant="body2">
                {textRunSummary
                  ? `Tekstiä käsitellään… ${textRunSummary.completed}/${textRunSummary.total}`
                  : 'Tekstiä käsitellään…'}
              </Typography>
              {textUnitSummary && textUnitSummary.total > 0 && (
                <Typography variant="caption" color="GrayText" sx={{ display: 'block' }}>
                  Osat {textUnitSummary.processed}/{textUnitSummary.total}
                </Typography>
              )}
              {textRunIds.length > 1 ? (
                <Typography variant="caption" color="GrayText" sx={{ display: 'block', mt: 0.25 }}>
                  Useita lukuja käsitellään rinnakkain
                </Typography>
              ) : (
                <Typography variant="caption" color="GrayText" sx={{ display: 'block', mt: 0.25 }}>
                  Käsittely käynnissä…
                </Typography>
              )}
              <Box sx={{ mt: 0.5, height: 6, bgcolor: 'action.hover', borderRadius: 999, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width:
                      textUnitSummary && textUnitSummary.total > 0
                        ? `${Math.min(100, Math.round((textUnitSummary.processed / textUnitSummary.total) * 100))}%`
                        : textRunSummary && textRunSummary.total && textRunSummary.total > 0
                          ? `${Math.min(100, Math.round((textRunSummary.completed / textRunSummary.total) * 100))}%`
                          : '100%',
                    opacity: textRunSummary || (textUnitSummary && textUnitSummary.total > 0) ? 1 : 0.3,
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          )}
          {audioProgress && (
            <Box
              sx={{
                p: 1,
                border: '1px solid rgba(0,0,0,0.16)',
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 2,
              }}
            >
              <Typography variant="body2">
                {t('csChapters.audioRunProgress', {
                  done: audioProgress.completed,
                  total: audioProgress.total,
                })}
              </Typography>
              {audioRunDetail?.status === 'running' && audioProgress.chunkTotal && audioProgress.chunkTotal > 0 && (
                <Typography variant="caption" color="GrayText" sx={{ display: 'block' }}>
                  Osat {audioProgress.chunkProcessed ?? 0}/{audioProgress.chunkTotal}
                </Typography>
              )}
              <Box sx={{ mt: 0.5, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <MuiButton
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={async () => {
                    await onCancelAllAudio();
                  }}
                  disabled={!!audioUi?.cancelling}
                >
                  Peruuta
                </MuiButton>
              </Box>
              <Box sx={{ mt: 0.5, height: 6, bgcolor: 'action.hover', borderRadius: 999, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width:
                      audioRunDetail?.status === 'running' && audioProgress.chunkTotal && audioProgress.chunkTotal > 0
                        ? `${Math.min(
                            100,
                            Math.round(((audioProgress.chunkProcessed ?? 0) / (audioProgress.chunkTotal || 1)) * 100)
                          )}%`
                        : audioProgress.total > 0
                          ? `${Math.min(100, Math.round((audioProgress.completed / audioProgress.total) * 100))}%`
                          : '0%',
                    opacity: 1,
                    bgcolor: 'primary.main',
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
      <Dialog open={confirmProcOpen} onClose={() => setConfirmProcOpen(false)}>
        <DialogTitle>{t('csChapters.dialogProcessTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('csChapters.dialogProcessBody', {
              title: selectedChapter?.title ?? selectedChapter?.name ?? t('csChapters.chapter'),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmProcOpen(false)} disabled={actionBusy}>
            {t('cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              await onProcess();
              setConfirmProcOpen(false);
            }}
            disabled={actionBusy}
          >
            {t('csChapters.process')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmExportOpen} onClose={() => (exportingMp3 ? void 0 : setConfirmExportOpen(false))}>
        <DialogTitle>{t('csChapters.exportAudioDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('csChapters.exportAudioDialogBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmExportOpen(false)} disabled={exportingMp3}>
            {t('cancel')}
          </MuiButton>
          <MuiButton variant="contained" onClick={() => void handleExportMp3()} disabled={exportingMp3}>
            {exportingMp3 ? t('csChapters.exporting') : t('csChapters.exportAudioConfirm')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmGenOpen} onClose={() => setConfirmGenOpen(false)}>
        <DialogTitle>{t('csChapters.dialogGenerateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('csChapters.dialogGenerateBody', {
              title: selectedChapter?.title ?? selectedChapter?.name ?? t('csChapters.chapter'),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmGenOpen(false)} disabled={actionBusy}>
            {t('cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              setConfirmGenOpen(false);
              // fire and forget to avoid dialog feeling "stuck"
              void onGenerate();
            }}
            disabled={actionBusy}
          >
            {t('csChapters.generate')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmGenAllOpen} onClose={() => setConfirmGenAllOpen(false)}>
        <DialogTitle>{t('csChapters.dialogGenerateAllTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{t('csChapters.dialogGenerateAllBody')}</Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {t('csChapters.voiceMenuButton', { voice: selectedVoiceLabel })}
            </Typography>
            <Select
              size="small"
              value={selectedVoice}
              displayEmpty
              fullWidth
              onChange={(e) => setSelectedVoice(String(e.target.value))}
              sx={{ mt: 1 }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <em>{t('csChapters.defaultVoice')}</em>
                </Box>
              </MenuItem>
              {voices.map((v) => (
                <MenuItem key={v.voice_id} value={v.voice_id}>
                  <Typography variant="body2">{v.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="GrayText">
              {t('csChapters.previewVoiceList')}
            </Typography>
            {renderVoiceList(320)}
          </Box>
          {generatingAll && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="GrayText">
                {t('csChapters.generateAllStarting', { total: genAllTotal })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmGenAllOpen(false)} disabled={generatingAll}>
            {t('cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={() => {
              setConfirmGenAllOpen(false);
              void onGenerateAll();
            }}
            disabled={generatingAll}
          >
            {t('csChapters.generateAll')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmProcAllOpen} onClose={() => setConfirmProcAllOpen(false)}>
        <DialogTitle>{t('csChapters.dialogProcessAllTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('csChapters.dialogProcessAllBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmProcAllOpen(false)} disabled={actionBusy}>
            {t('cancel')}
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={async () => {
              await onProcessAll();
              setConfirmProcAllOpen(false);
            }}
            disabled={actionBusy}
          >
            {t('csChapters.processAll')}
          </MuiButton>
        </DialogActions>
      </Dialog>
      {audioUrl && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            bgcolor: 'background.paper',
            borderTop: '1px solid rgba(0,0,0,0.12)',
            px: 2,
            py: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="GrayText" sx={{ minWidth: 120 }}>
              {selectedChapter?.title ?? selectedChapter?.name ?? t('csChapters.chapter')}
            </Typography>
            <audio
              ref={audioRef}
              controls
              src={audioUrl}
              style={{ width: '100%' }}
              crossOrigin="anonymous"
              key={`sticky-${audioUrl}`}
            />
          </Box>
        </Box>
      )}
    </>
  );
});

export default ChaptersScreen;
